import axios from 'axios';
import { api } from '@/api/client';
import type {
  TeamUser,
  CreateTeamUserRequest,
  UpdateTeamUserRequest,
  TeamUserApiResponse,
} from '@/pages/merchant/Teams/types';

import type {
  LoginCredentials,
} from '@/types/auth';
import { clearAuthCookiesService } from './clearAuthCookiesService';

// Permission and Role Types
export interface Permission {
  uuid: string;
  permission_name: string;
  description: string;
  section: string;
  is_merchant: boolean;
}

export interface Role {
  uuid: string;
  role_name: string;
  is_merchant: boolean;
  merchant: number;
  permissions: Permission[];
  created_at?: string;
  user_count?: number;
  
}

export interface CreateRoleRequest {
  permission_list: string[];
  role_name: string;
  role_description?: string;
}

export interface PermissionListResponse {
  success: boolean;
  message: string;
  data: Permission[];
}

export interface RoleResponse {
  success: boolean;
  message: string;
  data: Role;
}

export interface RolesListResponse {
  success: boolean;
  message: string;
  data: Role[];
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

// Team User API endpoints
const TEAM_USER_ENDPOINTS = {
  TEAM_LOGIN: (teamId: string) => `/merchant/v1/team-login/${teamId}/`,
  LIST: '/merchant/v1/team-users/',
  CREATE: '/merchant/v1/team-users/',
  LOGOUT: '/merchant/v1/team-logout/',
  UPDATE: (id: string) => `/merchant/v1/team-users/${id}/`,
  DELETE: (id: string) => `/merchant/v1/team-users/${id}/`,
  GET_BY_ID: (id: string) => `/merchant/v1/team-users/${id}/`,
  FILTER: '/merchant/v1/team-users/',
  PERMISSION_LIST: '/merchant/v1/permission-list',
  CREATE_ROLE: '/merchant/v1/create-role',
  GET_ROLES: '/merchant/v1/get-roles',
} as const;

class TeamUserService {
  private apiClient;

  constructor() {
    this.apiClient = axios.create({
      baseURL: API_BASE_URL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },

      // Enable cookies with credentials
      timeout: 10000,

    });
    // Add response interceptor for error handling
    this.apiClient.interceptors.response.use(
      response => response,
      error => {
        const status = error.response?.status;
        if (status === 401 || status === 403) {
          // Use the service to clear ONLY team user auth data and redirect
          clearAuthCookiesService.teamUserLogout(false);
          // Emit team logout event
          window.dispatchEvent(new CustomEvent('teamUser:teamUserlogout'));
        }
        return Promise.reject(error);
      }
    );
  }

  /**
 * Team login with email and password
 */
  async teamLogin(teamId: string, credentials: LoginCredentials): Promise<any> {
    try {
      const response = await api.post(TEAM_USER_ENDPOINTS.TEAM_LOGIN(teamId), credentials);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Team login failed');
    }
  }

  /**
   * Get paginated list of team users
   */
  async getTeamUsers(page: number = 1, pageSize: number = 50): Promise<any> {
    try {
      const url = `${TEAM_USER_ENDPOINTS.LIST}`;
      const response = await api.get<any>(url, {
        params: {
          page: page,
          page_size: pageSize,
        },
      });

      return response;

    } catch (error) {
      throw this.handleError(error, 'Failed to fetch team users');
    }
  }

  /**
   * Get single team user by ID
   */
  async getTeamUserById(id: string): Promise<TeamUser> {
    try {
      const response = await api.get<any>(TEAM_USER_ENDPOINTS.GET_BY_ID(id));
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch team user');
    }
  }

  /**
   * Create new team user
   */
  async createTeamUser(data: CreateTeamUserRequest): Promise<TeamUser> {
    try {
      const response = await api.post<TeamUserApiResponse>(TEAM_USER_ENDPOINTS.CREATE, data);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to create team user');
    }
  }

  /**
   * Update existing team user
   */
  async updateTeamUser(id: string, data: UpdateTeamUserRequest): Promise<TeamUser> {
    try {
      const response = await api.patch<TeamUserApiResponse>(TEAM_USER_ENDPOINTS.UPDATE(id), data);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to update team user');
    }
  }

  /**
   * Get team user data by name or email
   */
  async getTeanUserData(name?: string, email?: string): Promise<any> {
    try {
      if (!name && !email) {
        throw new Error('Either name or email must be provided');
      }

      const queryParams = new URLSearchParams();

      if (name) {
        queryParams.append('name', name);
      }

      if (email) {
        queryParams.append('email', email);
      }

      const url = `${TEAM_USER_ENDPOINTS.FILTER}?${queryParams.toString()}`;
      const response = await api.get<{ data: any }>(url);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch team user data');
    }
  }

  /**
   * Delete team user
   */
  async deleteTeamUser(id: string): Promise<void> {
    try {
      await api.delete(TEAM_USER_ENDPOINTS.DELETE(id));
    } catch (error) {
      throw this.handleError(error, 'Failed to delete team user');
    }
  }

  /**
   * Get permission list
   */
  async getPermissionList(): Promise<Permission[]> {
    try {
      const response = await api.get<PermissionListResponse>(TEAM_USER_ENDPOINTS.PERMISSION_LIST);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch permission list');
    }
  }

  /**
   * Create a new role
   */
  async createRole(data: CreateRoleRequest): Promise<Role> {
    try {
      const response = await api.post<RoleResponse>(TEAM_USER_ENDPOINTS.CREATE_ROLE, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to create role');
    }
  }

  /**
   * Get all roles
   */
  async getRoles(): Promise<Role[]> {
    try {
      const response = await api.get<RolesListResponse>(TEAM_USER_ENDPOINTS.GET_ROLES);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch roles');
    }
  }


  /**
   * Logout team user: clear only team-related data, preserve main auth user
   */
  async teamUserlogout(): Promise<void> {
    try {
      // Call logout endpoint which will clear the server-side cookies
      await api.post(TEAM_USER_ENDPOINTS.LOGOUT);
    } catch (error) {
      // Ignore logout errors but log for debugging
      console.warn('Team logout request failed:', error);
    } finally {
      // Clear ONLY team user auth data (cookies + localStorage)
      clearAuthCookiesService.clearTeamUserAuthData();
    }
  }

  /**
   * Clear only team-related stored authentication data
   * This method is deprecated - auth state should be managed by Redux
   */
  private clearStoredAuth(): void {
    // No longer clearing localStorage - auth state managed by Redux
    // This method is kept for compatibility but does nothing
  }

  /**
   * Handle API errors
   */
  private handleError(error: unknown, defaultMessage: string): Error {
    if (error && typeof error === 'object') {
      if ('response' in error && error.response && typeof error.response === 'object' && 'data' in error.response) {
        const responseData = error.response.data as any;
        if (responseData?.message) {
          return new Error(responseData.message);
        }
      }
      if ('message' in error) {
        return error as Error;
      }
    }
    return new Error(defaultMessage);
  }
}

// Create singleton instance
export const teamUserService = new TeamUserService();

// Export for testing
export { TeamUserService };
